import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Loader2, MapPin } from 'lucide-react';
import { fetchPostalCodeLocalities, type PostalCodeLocality } from '@/hooks/useAddressValidation';

/**
 * Postal-code sanity check, shown ONLY while a reviewer is CHANGING a postal code (user ask 2026-08-28 —
 * explicitly not on creation). It answers "which commune and which village does this code belong to?"
 * from the DMP's own address referential, and lets the reviewer apply the right city in one click.
 *
 * Two rules drive the whole design:
 *  1. A postal code is NOT one village. 5190 covers eight of them under a single commune. So when several
 *     are known the component ALWAYS shows them and asks — it never picks for the reviewer. That is the
 *     case the user asked to make visible: "on peut avoir deux cities, il faut que ça apparaisse".
 *  2. Silence beats a false alarm. Outside Unifiber's footprint the DMP knows nothing about the code, and
 *     that is reported as "no answer", never as "wrong" — a check that cries wolf is a check people learn
 *     to ignore. Same for the long tail: rare pairs are listed apart instead of being treated as errors.
 *
 * The commune is asserted (it is single in practice), the city is offered. Nothing here writes anything:
 * picking a value calls back into the parent form, which the reviewer still has to save.
 */
export function PostalCodeLocalityHint({
  postalCode,
  city,
  active,
  disabled = false,
  onPick,
}: {
  postalCode: string;
  city: string;
  /** True only while the postal code differs from the value the form loaded — this is a MODIFICATION aid. */
  active: boolean;
  disabled?: boolean;
  onPick: (city: string, municipality: string) => void;
}) {
  const { t } = useTranslation(['tickets']);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<{ total: number; localities: PostalCodeLocality[] } | null>(null);

  const code = postalCode.trim();
  const lookupable = active && /^[0-9]{4}$/.test(code);

  useEffect(() => {
    if (!lookupable) { setData(null); setLoading(false); return; }
    // Debounced + race-guarded: the reviewer types the four digits one by one, so every keystroke would
    // otherwise fire a query and the answers could land out of order.
    let alive = true;
    setLoading(true);
    const timer = setTimeout(() => {
      fetchPostalCodeLocalities(code)
        .then((res) => { if (alive) { setData(res.data); setLoading(false); } })
        .catch(() => { if (alive) { setData(null); setLoading(false); } });
    }, 350);
    return () => { alive = false; clearTimeout(timer); };
  }, [code, lookupable]);

  if (!lookupable) return null;

  if (loading) {
    return (
      <p className="flex items-center gap-1.5 text-[11px] text-slate-400">
        <Loader2 className="w-3 h-3 animate-spin" />{t('tickets:validation.postalCheck.checking')}
      </p>
    );
  }
  if (!data) return null;

  // Unknown code: say so plainly and stop. NOT a warning — see rule 2 above.
  if (data.total === 0 || data.localities.length === 0) {
    return (
      <p className="flex items-start gap-1.5 text-[11px] text-slate-400">
        <MapPin className="w-3 h-3 shrink-0 mt-0.5" />{t('tickets:validation.postalCheck.unknown', { postalCode: code })}
      </p>
    );
  }

  const same = (a: string, b: string) => a.trim().localeCompare(b.trim(), 'fr', { sensitivity: 'base' }) === 0;
  const common = data.localities.filter((l) => l.common);
  const rare = data.localities.filter((l) => !l.common);
  const shown = common.length > 0 ? common : data.localities;
  const communes = [...new Set(shown.map((l) => l.municipality))];
  const typed = city.trim();
  const matches = typed !== '' && shown.some((l) => same(l.city, typed));

  const chips = (
    <div className="flex flex-wrap gap-1.5 mt-1.5">
      {shown.map((l) => {
        const current = same(l.city, typed);
        return (
          <button
            key={l.city + '|' + l.municipality}
            type="button"
            disabled={disabled}
            onClick={() => onPick(l.city, l.municipality)}
            className={
              // whitespace-nowrap: a locality is ONE name — "Tourinnes-la-Grosse" broken across two
              // lines reads as two places (user report 2026-08-28). The row wraps, the name never does.
              'text-[11px] px-2 py-0.5 rounded-full border transition-colors disabled:opacity-50 whitespace-nowrap ' +
              (current
                ? 'bg-emerald-100 border-emerald-300 text-emerald-800 font-semibold'
                : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50')
            }
            title={t('tickets:validation.postalCheck.pick', { city: l.city, municipality: l.municipality })}
          >
            {l.city}
            <span className="ml-1 text-slate-400">({l.municipality})</span>
          </button>
        );
      })}
    </div>
  );

  // The city does not match anything the code is known to cover: the one case worth an amber alarm.
  if (!matches) {
    return (
      <div className="rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2" role="alert">
        <p className="flex items-start gap-1.5 text-[11px] text-amber-800">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          {typed
            ? t('tickets:validation.postalCheck.mismatch', { city: typed, postalCode: code })
            : t('tickets:validation.postalCheck.missingCity', { postalCode: code })}
        </p>
        {chips}
        {rare.length > 0 && (
          <p className="mt-1.5 text-[10px] text-amber-700/70">
            {t('tickets:validation.postalCheck.rare', { count: rare.length, list: rare.map((l) => l.city).join(', ') })}
          </p>
        )}
      </div>
    );
  }

  // Several villages behind one code — the reviewer must SEE the alternatives even when the typed value
  // is already one of them (this is the "deux cities pour un même code postal" case).
  if (shown.length > 1) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2">
        <p className="flex items-start gap-1.5 text-[11px] text-slate-600">
          <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          {t('tickets:validation.postalCheck.multiple', { postalCode: code, count: shown.length, municipality: communes.join(' / ') })}
        </p>
        {chips}
      </div>
    );
  }

  // One village, and it is the one typed: a quiet confirmation, no action needed.
  return (
    <p className="flex items-start gap-1.5 text-[11px] text-emerald-700">
      <Check className="w-3.5 h-3.5 shrink-0 mt-0.5" />
      {t('tickets:validation.postalCheck.single', { postalCode: code, city: shown[0].city, municipality: shown[0].municipality })}
    </p>
  );
}
